import * as Dialog from "@radix-ui/react-dialog";
import { IoClose } from "react-icons/io5";
import { useConfirmation } from "./useConfirmation";
import "./ConfirmationDialog.scss";

/**
 * Accessible confirmation modal on Radix Dialog. Uses z-index above the shared Drawer
 * so it stacks correctly when opened from drawer footers or the main queue chrome.
 *
 * When `onConfirm` returns a Promise (e.g. `mutation.mutateAsync()`), the dialog shows a loading
 * state on the primary button, disables all dismissal controls until the promise settles, then closes.
 * Synchronous `onConfirm` (void / non-thenable) closes immediately without a loading flash.
 *
 * @param {object} props
 * @param {boolean} props.open Controlled open state.
 * @param {(open: boolean) => void} props.onOpenChange Radix callback; fired when overlay, Escape, Cancel, or header close is used (blocked while confirm is in flight).
 * @param {string} props.title Dialog heading.
 * @param {string} props.body Message shown under the title.
 * @param {string} props.confirmLabel Primary action button label when idle.
 * @param {string} [props.confirmPendingLabel] Primary button label while `onConfirm` promise is pending (defaults to `confirmLabel`).
 * @param {(() => void) | (() => Promise<unknown>)} props.onConfirm Runs when the primary button is clicked; if it returns a thenable, the dialog waits before calling `onOpenChange(false)`.
 * @param {string} [props.contentClassName] Extra class names on `Dialog.Content`.
 * @returns {import('react').JSX.Element | null}
 */
const ConfirmationDialog = ({
    open,
    onOpenChange,
    title,
    body,
    confirmLabel,
    confirmPendingLabel,
    onConfirm,
    contentClassName,
}) => {
    const {
        isConfirmLoading,
        visibleContent,
        pendingLabel,
        contentClasses,
        handleRootOpenChange,
        handleConfirmPrimary,
        dismiss,
        blockDismissWhileLoading,
    } = useConfirmation({
        open,
        onOpenChange,
        title,
        body,
        confirmLabel,
        confirmPendingLabel,
        onConfirm,
        contentClassName,
    });

    return (
        <Dialog.Root open={open} onOpenChange={handleRootOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="confirmation-dialog__overlay" />
                <Dialog.Content
                    className={contentClasses}
                    onPointerDownOutside={blockDismissWhileLoading}
                    onInteractOutside={blockDismissWhileLoading}
                    onEscapeKeyDown={blockDismissWhileLoading}
                >
                    <div className="confirmation-dialog__header">
                        <Dialog.Title className="confirmation-dialog__title">
                            {visibleContent.title}
                        </Dialog.Title>
                        <button
                            type="button"
                            className="confirmation-dialog__close"
                            aria-label="Close dialog"
                            disabled={isConfirmLoading}
                            onClick={dismiss}
                        >
                            <IoClose aria-hidden size={22} />
                        </button>
                    </div>
                    <Dialog.Description className="confirmation-dialog__body">
                        {visibleContent.body}
                    </Dialog.Description>
                    <div className="confirmation-dialog__footer">
                        <button
                            type="button"
                            className="confirmation-dialog__btn confirmation-dialog__btn--secondary"
                            disabled={isConfirmLoading}
                            onClick={dismiss}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="confirmation-dialog__btn confirmation-dialog__btn--primary"
                            disabled={isConfirmLoading}
                            aria-busy={isConfirmLoading}
                            onClick={handleConfirmPrimary}
                        >
                            {isConfirmLoading ? (
                                <span className="confirmation-dialog__confirm-inner">
                                    <span>{pendingLabel}</span>
                                    <span
                                        className="confirmation-dialog__ellipsis"
                                        aria-hidden="true"
                                    >
                                        <span className="confirmation-dialog__ellipsis-dot" />
                                        <span className="confirmation-dialog__ellipsis-dot" />
                                        <span className="confirmation-dialog__ellipsis-dot" />
                                    </span>
                                </span>
                            ) : (
                                visibleContent.confirmLabel
                            )}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export default ConfirmationDialog;
